import {
  useSurfacePreferenceProperty,
  useUpdateSurfacePreferences,
} from "@/components/preferences/usePreferences.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import { hasDifferentUtcOffset } from "@/shared/timezones.ts";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useLocation } from "wouter";

const DISMISSED_STORAGE_KEY = "timezone_switch_prompt_dismissed";

/**
 * Per-account map of the device timezone the user was last asked about, so a
 * decline silences the prompt for *this* trip but a later trip to a different
 * timezone asks again. Device-local by design — a decision made on a laptop
 * shouldn't silence the prompt on a phone that may genuinely be elsewhere.
 */
type DismissedByAccount = Record<string, string>;

const readDismissed = (accountId: number | undefined): string | undefined => {
  if (accountId === undefined) return undefined;
  try {
    const stored = localStorage.getItem(DISMISSED_STORAGE_KEY);
    if (!stored) return undefined;
    const parsed: DismissedByAccount = JSON.parse(stored);
    return parsed[String(accountId)];
  } catch (error) {
    console.error("Failed to read dismissed timezone prompt:", error);
    return undefined;
  }
};

const writeDismissed = (accountId: number, timezone: string) => {
  try {
    const stored = localStorage.getItem(DISMISSED_STORAGE_KEY);
    const parsed: DismissedByAccount = stored ? JSON.parse(stored) : {};
    localStorage.setItem(
      DISMISSED_STORAGE_KEY,
      JSON.stringify({ ...parsed, [String(accountId)]: timezone }),
    );
  } catch (error) {
    console.error("Failed to persist dismissed timezone prompt:", error);
  }
};

/** e.g. "Central European Summer Time (GMT+02:00)", falling back to the raw id. */
const describeTimezone = (timezoneId: string, at: Date): string => {
  try {
    const longName = new Intl.DateTimeFormat("en-US", {
      timeZone: timezoneId,
      timeZoneName: "long",
    })
      .formatToParts(at)
      .find((part) => part.type === "timeZoneName")?.value;
    const offset = new Intl.DateTimeFormat("en-US", {
      timeZone: timezoneId,
      timeZoneName: "longOffset",
    })
      .formatToParts(at)
      .find((part) => part.type === "timeZoneName")?.value;
    if (longName && offset) return `${longName} (${offset})`;
    return longName ?? timezoneId;
  } catch (_error) {
    return timezoneId;
  }
};

/**
 * Asks a traveling user whether future communications should use the timezone
 * they're actually in. Mounted globally so it fires on the first authenticated
 * render after login (and on any later landing-page render) rather than being
 * tied to one view.
 *
 * Only surfaces when the device sits at a genuinely different UTC offset than
 * the timezone their Location settings resolve to — saying yes flips the
 * preference to "current", saying no leaves it on home. Either answer is
 * remembered so the prompt doesn't reappear every login.
 */
export const TimezoneSwitchPrompt = () => {
  const [path] = useLocation();
  const { userContext } = useCurrentUserContext();
  const timezonePreference = useSurfacePreferenceProperty("location", "timezonePreference");
  const updateLocationPreferences = useUpdateSurfacePreferences("location");
  const queryClient = useQueryClient();

  const accountId = userContext?.user.id;
  // Re-derived when the user-context query resolves `accountId`, so a cold load
  // that mounted with it still undefined doesn't miss a stored dismissal.
  const persistedDismissed = useMemo(() => readDismissed(accountId), [accountId]);
  // The timezone answered this mount, kept in state so answering re-renders and
  // closes the dialog before the persisted read catches up.
  const [sessionDismissed, setSessionDismissed] = useState<string>();
  const dismissedTimezone = sessionDismissed ?? persistedDismissed;
  const [isUpdating, setIsUpdating] = useState(false);

  // Read once per mount: a session-long clock isn't needed, and re-deriving on
  // every render would restart the dialog animation.
  const [now] = useState(() => new Date());
  const deviceTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const homeTimezone = userContext?.user.userContact.locations?.find(
    (l) => l.type === "home",
  )?.timezoneId;

  // The timezone the Location settings currently resolve to. Under the
  // "current" preference that *is* the device timezone, so there is nothing to
  // switch to and the prompt stays closed.
  const settingsTimezone = timezonePreference === "home" ? homeTimezone : undefined;

  const isAwayFromSettings = useMemo(
    () => !!settingsTimezone && hasDifferentUtcOffset(settingsTimezone, deviceTimezone, now),
    [settingsTimezone, deviceTimezone, now],
  );

  // The Location settings page offers the same choice inline, so a modal on top
  // of it would be redundant — and dismissing it there would silence the prompt
  // for a user who is actively editing the setting anyway.
  const isOnLocationSettings = path.startsWith("/settings/location");

  const isOpen =
    !isOnLocationSettings && isAwayFromSettings && dismissedTimezone !== deviceTimezone;

  const remember = useCallback(() => {
    if (accountId !== undefined) writeDismissed(accountId, deviceTimezone);
    setSessionDismissed(deviceTimezone);
  }, [accountId, deviceTimezone]);

  const handleKeepHome = useCallback(() => {
    if (isUpdating) return;
    remember();
  }, [isUpdating, remember]);

  const handleSwitchToCurrent = useCallback(async () => {
    setIsUpdating(true);
    try {
      await updateLocationPreferences({ timezonePreference: "current" });
      // Both the agenda and the calendar grid render event times in the
      // effective timezone, and they live under separate query keys — refetch
      // both so nothing keeps showing home-timezone times after the switch.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["dailyAgenda"] }),
        queryClient.invalidateQueries({ queryKey: ["calendarEvents"] }),
      ]);
      remember();
    } catch (_error) {
      errorToast("Failed to update timezone preference. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  }, [updateLocationPreferences, queryClient, remember]);

  if (!isOpen) return null;

  return (
    <Dialog open onOpenChange={(open) => !open && handleKeepHome()}>
      <DialogContent className="max-w-180">
        <DialogHeader>
          <DialogTitle>You're in a different timezone</DialogTitle>
          <DialogDescription>
            This device is in {describeTimezone(deviceTimezone, now)}, but scheduling and
            communications currently use your home timezone,{" "}
            {settingsTimezone ? describeTimezone(settingsTimezone, now) : "your home location"}.
            Switch to your current timezone for future communications?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleKeepHome}
            disabled={isUpdating}
            data-statsig-name="timezone-switch-prompt-keep-home"
          >
            Keep home timezone
          </Button>
          <Button
            type="button"
            onClick={handleSwitchToCurrent}
            disabled={isUpdating}
            data-statsig-name="timezone-switch-prompt-use-current"
          >
            {isUpdating ? "Switching..." : "Use current timezone"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
